// DEPENDENCIES ---------------------------------------------------------------- //

// import React from 'react';

import { css } from '@emotion/css';
import { Theme, Typography, useTheme } from '@mui/material';
import {
  BiSolidCalendar,
  BiSolidCalendarCheck,
  BiSolidCalendarEdit,
  BiSolidCalendarExclamation,
  BiSolidCalendarX,
} from 'react-icons/bi';

import { Metric } from '@features/missingRefReport/providers/MissingRefReportProvider';

// TYPES ---------------------------------------------------------------- //

interface AggregateCounts {
  allMoves: number;
  upcomingMoves: number;
  inProgressMoves: number;
  completedMoves: number;
  canceledMoves: number;
}

interface MissingRefReportMetricsProps {
  aggregateCounts: AggregateCounts;
  metric: Metric;
  onMetricChange: (newMetric: Metric) => void;
}

// COMPONENT ---------------------------------------------------------------- //

const MissingRefReportMetrics = ({ aggregateCounts, metric, onMetricChange }: MissingRefReportMetricsProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const clsx = useSxStyles(theme);

  // Use aggregate counts for display
  const { allMoves, upcomingMoves, inProgressMoves, completedMoves, canceledMoves } = aggregateCounts;

  const handleMetricClick = (selectedMetric: Metric) => {
    onMetricChange(selectedMetric);
  };

  return (
    <div className={cls.metrics}>
      <div
        className={`${cls.metricCard} ${metric === 'all' ? cls.metricCardAllActive : ''}`}
        onClick={() => handleMetricClick('all')}
      >
        <div className={cls.metricContent}>
          <BiSolidCalendar size={40} color={theme.palette.utility.main} />

          <div>
            <Typography variant='h3' sx={clsx.metricNumber}>
              {allMoves}
            </Typography>

            <Typography variant='body2' sx={clsx.metricLabel}>
              All Moves
            </Typography>
          </div>
        </div>
      </div>

      <div
        className={`${cls.metricCard} ${metric === 'upcoming' ? cls.metricCardUpcomingActive : ''}`}
        onClick={() => handleMetricClick('upcoming')}
      >
        <div className={cls.metricContent}>
          <BiSolidCalendarEdit size={40} color={theme.palette.info.main} />

          <div>
            <Typography variant='h3' sx={clsx.metricNumber}>
              {upcomingMoves}
            </Typography>

            <Typography variant='body2' sx={clsx.metricLabel}>
              Upcoming Moves
            </Typography>
          </div>
        </div>
      </div>

      <div
        className={`${cls.metricCard} ${metric === 'inprogress' ? cls.metricCardInProgressActive : ''}`}
        onClick={() => handleMetricClick('inprogress')}
      >
        <div className={cls.metricContent}>
          <BiSolidCalendarExclamation size={40} color={theme.palette.warning.main} />

          <div>
            <Typography variant='h3' sx={clsx.metricNumber}>
              {inProgressMoves}
            </Typography>

            <Typography variant='body2' sx={clsx.metricLabel}>
              In-Progress Moves
            </Typography>
          </div>
        </div>
      </div>

      <div
        className={`${cls.metricCard} ${metric === 'completed' ? cls.metricCardCompletedActive : ''}`}
        onClick={() => handleMetricClick('completed')}
      >
        <div className={cls.metricContent}>
          <BiSolidCalendarCheck size={40} color={theme.palette.success.main} />

          <div>
            <Typography variant='h3' sx={clsx.metricNumber}>
              {completedMoves}
            </Typography>

            <Typography variant='body2' sx={clsx.metricLabel}>
              Completed Moves
            </Typography>
          </div>
        </div>
      </div>

      <div
        className={`${cls.metricCard} ${metric === 'canceled' ? cls.metricCardCanceledActive : ''}`}
        onClick={() => handleMetricClick('canceled')}
      >
        <div className={cls.metricContent}>
          <BiSolidCalendarX size={40} color={theme.palette.error.main} />

          <div>
            <Typography variant='h3' sx={clsx.metricNumber}>
              {canceledMoves}
            </Typography>

            <Typography variant='body2' sx={clsx.metricLabel}>
              Canceled Moves
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  metrics: css`
    display: flex;
    gap: 16px;
  `,

  metricCard: css`
    padding: 24px;
    border: 1px solid ${theme.palette.divider};
    border-radius: 10px;
    outline: 2px solid transparent;
    outline-offset: -2px;
    background-color: ${theme.palette.background.default};
    cursor: pointer;
    transition: all 0.15s ease-in-out;

    &:hover {
      background-color: ${theme.palette.background.alt};
    }
  `,

  metricCardAllActive: css`
    outline-color: ${theme.palette.utility.main};
    background-color: ${theme.palette.utility.main}20;
    &:hover {
      background-color: ${theme.palette.utility.main}30;
    }
  `,
  metricCardUpcomingActive: css`
    outline-color: ${theme.palette.info.main};
    background-color: ${theme.palette.info.main}20;
    &:hover {
      background-color: ${theme.palette.info.main}30;
    }
  `,
  metricCardInProgressActive: css`
    outline-color: ${theme.palette.warning.main};
    background-color: ${theme.palette.warning.main}20;
    &:hover {
      background-color: ${theme.palette.warning.main}30;
    }
  `,
  metricCardCompletedActive: css`
    outline-color: ${theme.palette.success.main};
    background-color: ${theme.palette.success.main}20;
    &:hover {
      background-color: ${theme.palette.success.main}30;
    }
  `,
  metricCardCanceledActive: css`
    outline-color: ${theme.palette.error.main};
    background-color: ${theme.palette.error.main}20;
    &:hover {
      background-color: ${theme.palette.error.main}30;
    }
  `,

  metricContent: css`
    display: flex;
    align-items: center;
    gap: 16px;
  `,
});

const useSxStyles = (theme: Theme) => ({
  metricNumber: {
    lineHeight: 1,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  metricLabel: {
    lineHeight: 1,
    marginTop: `4px`,
    color: theme.palette.text.secondary,
  },
});

// EXPORT ---------------------------------------------------------------- //

export default MissingRefReportMetrics;
